import { Content, Header, Link, Page, SidebarItem, sidebarConfig } from '@backstage/core-components';
import { ConfigApi, IconComponent, configApiRef, useApi } from '@backstage/core-plugin-api';
import { useFeatureFlags } from '@internal/plugin-launchdarkly-react';
import { Card, CardActionArea, CardContent, Grid, Typography, makeStyles } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import AppsIcon from '@material-ui/icons/Apps';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import ExtensionIcon from '@material-ui/icons/Extension';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import StorageIcon from '@material-ui/icons/Storage';
import { Link as RouterLink, useLocation } from 'react-router-dom';

const DEFAULT_AI_SOLUTIONS_PATH = '/docs/default/component/cae-ai-solutions-catalog';

/** The old sidebar setting remains a fallback for existing deployments. */
export function getAiSolutionsPath(config: ConfigApi): string {
  return (
    config.getOptionalString('devhub.catalogs.aiSolutionsPath') ??
    config.getOptionalString('devhub.sidebar.aiSolutionsCatalogPath') ??
    DEFAULT_AI_SOLUTIONS_PATH
  );
}

function isWithin(pathname: string, root: string): boolean {
  if (/^https?:\/\//i.test(root)) return false;
  const path = root.replace(/\/+$/, '');
  return pathname === path || pathname.startsWith(`${path}/`);
}

/** Matches whole path segments, including entity pages and AI Solutions subpages. */
export function isCatalogsPath(pathname: string, aiSolutionsPath: string): boolean {
  return ['/catalogs', '/catalog', '/api-docs', aiSolutionsPath].some(path => isWithin(pathname, path));
}

interface CatalogChoice {
  title: string;
  description: string;
  path: string;
  icon: IconComponent;
}

function useCatalogChoices(): CatalogChoice[] {
  const flags = useFeatureFlags();
  const aiSolutionsPath = getAiSolutionsPath(useApi(configApiRef));
  return [
    {
      enabled: flags.idpPortalCatalog,
      title: 'Software Catalog',
      description: 'Explore services, components and systems, and find the teams that own them.',
      path: '/catalog',
      icon: StorageIcon,
    },
    {
      enabled: flags.idpPortalApis,
      title: 'APIs',
      description: 'Discover APIs, read their definitions and see how services connect.',
      path: '/api-docs',
      icon: ExtensionIcon,
    },
    {
      enabled: flags.idpPortalDocumentation,
      title: 'AI Solutions',
      description: 'Find AI tools and solutions to help you build, automate and deliver.',
      path: aiSolutionsPath,
      icon: EmojiObjectsIcon,
    },
  ].filter(choice => choice.enabled);
}

const useStyles = makeStyles(theme => ({
  cell: { display: 'flex' },
  card: {
    display: 'flex',
    width: '100%',
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px solid ${theme.palette.divider}`,
  },
  action: {
    display: 'flex',
    alignItems: 'stretch',
    color: theme.palette.text.primary,
    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
  },
  cardContent: { width: '100%', padding: theme.spacing(3) },
  icon: {
    display: 'inline-flex',
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
  },
  title: { fontWeight: 700, marginBottom: theme.spacing(1) },
  navigation: {
    padding: theme.spacing(1, 3),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  back: { display: 'inline-flex', alignItems: 'center', gap: theme.spacing(1) },
  active: {
    '&&': {
      color: theme.palette.navigation.selectedColor,
      boxShadow: `inset ${sidebarConfig.selectedIndicatorWidth}px 0 ${theme.palette.navigation.indicator}`,
    },
  },
}));

export function CatalogsSidebarItem() {
  const choices = useCatalogChoices();
  const aiSolutionsPath = getAiSolutionsPath(useApi(configApiRef));
  const { pathname } = useLocation();
  const classes = useStyles();
  if (!choices.length) {
    return null;
  }
  return (
    <SidebarItem
      icon={AppsIcon}
      text="Catalogs"
      to="/catalogs"
      className={!isWithin(pathname, '/catalogs') && isCatalogsPath(pathname, aiSolutionsPath) ? classes.active : undefined}
    />
  );
}

/**
 * Kept outside route elements so legacy createApp still discovers the Catalog
 * and API plugins directly in the route tree.
 */
export function CatalogsNavigation() {
  const choices = useCatalogChoices();
  const { pathname } = useLocation();
  const classes = useStyles();
  if (!choices.some(choice => isWithin(pathname, choice.path))) {
    return null;
  }
  return (
    <nav aria-label="Catalog navigation" className={classes.navigation}>
      <Link to="/catalogs" className={classes.back}>
        <ArrowBackIcon fontSize="small" />
        All catalogs
      </Link>
    </nav>
  );
}

export function CatalogsPage() {
  const choices = useCatalogChoices();
  const classes = useStyles();
  return (
    <Page themeId="home">
      <Header title="Catalogs" subtitle="Choose a catalog to explore" />
      <Content>
        <Grid container spacing={3} alignItems="stretch">
          {choices.map(({ title, description, path, icon: Icon }) => (
            <Grid item xs={12} sm={6} md={4} key={title} className={classes.cell}>
              <Card className={classes.card}>
                <CardActionArea
                  component={/^https?:\/\//i.test(path) ? 'a' : RouterLink}
                  {...(/^https?:\/\//i.test(path) ? { href: path, target: '_blank', rel: 'noopener noreferrer' } : { to: path })}
                  role="link"
                  className={classes.action}
                >
                  <CardContent className={classes.cardContent}>
                    <span className={classes.icon}>
                      <Icon fontSize="large" />
                    </span>
                    <Typography variant="h5" component="h2" className={classes.title}>
                      {title}
                      {/^https?:\/\//i.test(path) && <OpenInNewIcon fontSize="small" titleAccess="Opens in a new tab" />}
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                      {description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
        {!choices.length && <Typography color="textSecondary">No catalogs are enabled in this environment.</Typography>}
      </Content>
    </Page>
  );
}
